import { useState, createContext, useEffect, PropsWithChildren, useContext } from 'react'
import { IAllowedInputDates, IReportPeriodContext } from './report-periods.model';
import ReportPeriodService from '../../shared/services/report-period/report-period.service';
import { IReportPeriodResponse } from '../../shared/services/report-period/report-period.model';
import { UserDetailsContext } from '../user-details/user-details';
import Cookies from 'universal-cookie';

export const ReportPeriodContext = createContext<IReportPeriodContext>({} as IReportPeriodContext);

export const ReportPeriodProvider = ({ children }: PropsWithChildren) => {

  const cookies = new Cookies(null, { path: '/' });

  const reportPeriodService = new ReportPeriodService();
  const userDetailsContext = useContext(UserDetailsContext);

  const [allReportPeriods, setAllReportPeriods] = useState<IReportPeriodResponse[]>([]);
  const [openReportPeriods, setOpenReportPeriods] = useState<IReportPeriodResponse[]>([]);
  const [lockedReportPeriods, setLockedReportPeriods] = useState<IReportPeriodResponse[]>([]);

  const [lastSelectedReportId, setLastSelectedReportId] = useState<string | undefined>();
  const [lastSelectedOpenReportId, setLastSelectedOpenReportId] = useState<string | undefined>();
  const [lastSelectedLockedReportId, setLastSelectedLockedReportId] = useState<string | undefined>();

  const [change, setChange] = useState(0);
  const [reportPeriodsLoaded, setReportPeriodsLoaded] = useState(false);

  useEffect(() => {
    if (allReportPeriods?.length === 0 && userDetailsContext.userDetails?.id && !userDetailsContext.userDetails.emailVerificationRequired) {
      reportPeriodService.getAllReportPeriod()
        .then(response => {
          setAllReportPeriods(response);
          setOpenReportPeriods(response.filter(report => !report.locked));
          setLockedReportPeriods(response.filter(report => report.locked));
          setLastSelectedReportId(response.sort((b, a) => parseInt(a.lastSelectedDate as string) - parseInt(b.lastSelectedDate as string))[0]?.id)
          setLastSelectedOpenReportId(response.filter(report => !report.locked).sort((b, a) => parseInt(a.lastSelectedDate as string) - parseInt(b.lastSelectedDate as string))[0]?.id)
          setLastSelectedLockedReportId(response.filter(report => report.locked).sort((b, a) => parseInt(a.lastSelectedDate as string) - parseInt(b.lastSelectedDate as string))[0]?.id)
        })
        .catch(err => {
          console.log(err);
        })
        .finally(() => {
          setReportPeriodsLoaded(true)
        })
    } else if (!userDetailsContext.userDetails?.id) {
      setAllReportPeriods([]);
      setOpenReportPeriods([]);
      setLockedReportPeriods([]);
      setReportPeriodsLoaded(true)
    } else {
      setReportPeriodsLoaded(true)
    }
  }, [change, userDetailsContext.userDetails]);

  useEffect(() => {
    if (lastSelectedReportId) {
      reportPeriodService.updateLastSelectedDate(lastSelectedReportId)
        .then(() => {
          const formattedOpenReportPeriods = openReportPeriods.map(period => {
            if (period.id === lastSelectedReportId) {
              return { ...period, lastSelectedDate: Date.now() }
            } else {
              return period
            }
          })
          setOpenReportPeriods(formattedOpenReportPeriods)

          const formattedLockedReportPeriods = lockedReportPeriods.map(period => {
            if (period.id === lastSelectedReportId) {
              return { ...period, lastSelectedDate: Date.now() }
            } else {
              return period
            }
          })
          setLockedReportPeriods(formattedLockedReportPeriods)

          const formattedReportPeriods = allReportPeriods.map(period => {
            if (period.id === lastSelectedReportId) {
              return { ...period, lastSelectedDate: Date.now() }
            } else {
              return period
            }
          })
          setAllReportPeriods(formattedReportPeriods)
        })
        .catch(err => {
          console.log(err)
        })
    }
  }, [lastSelectedReportId])

  async function forceUpdateReportPeriods(): Promise<IReportPeriodResponse[]> {
    return new Promise(function (resolve, reject) {
      reportPeriodService.getAllReportPeriod()
        .then(response => {
          setAllReportPeriods(response);
          setOpenReportPeriods(response.filter(report => !report.locked));
          setLockedReportPeriods(response.filter(report => report.locked));
          setLastSelectedReportId(response.sort((b, a) => parseInt(a.lastSelectedDate as string) - parseInt(b.lastSelectedDate as string))[0]?.id)
          setLastSelectedOpenReportId(response.filter(report => !report.locked).sort((b, a) => parseInt(a.lastSelectedDate as string) - parseInt(b.lastSelectedDate as string))[0]?.id)
          setLastSelectedLockedReportId(response.filter(report => report.locked).sort((b, a) => parseInt(a.lastSelectedDate as string) - parseInt(b.lastSelectedDate as string))[0]?.id)

          resolve(response);
        })
        .catch(() => {
          reject();
        })
    })
  }

  function getAllowedInputDates(): IAllowedInputDates {
    return {
      startDate: allReportPeriods?.find(report => report.id === lastSelectedReportId)?.startDate,
      endDate: allReportPeriods?.find(report => report.id === lastSelectedReportId)?.endDate,
    }
  }

  function clearData(): void {
    setAllReportPeriods([])
    setOpenReportPeriods([]);
    setLockedReportPeriods([]);
    setLastSelectedReportId(undefined);
    setLastSelectedOpenReportId(undefined);
    setLastSelectedLockedReportId(undefined);
    setReportPeriodsLoaded(false);
    cookies.remove("gca_lat");
  };


  return (
    <ReportPeriodContext.Provider value={{
      reportPeriodsLoaded,
      allReportPeriods,
      openReportPeriods,
      lockedReportPeriods,
      lastSelectedReportId,
      lastSelectedLockedReportId,
      lastSelectedOpenReportId,
      setLastSelectedReportId,
      setLastSelectedLockedReportId,
      setLastSelectedOpenReportId,
      setChange,
      forceUpdateReportPeriods,
      getAllowedInputDates,
      clearData,
    }}>
      {children}
    </ReportPeriodContext.Provider>
  )
}